import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Wallet, CreditCard, Banknote, Landmark, PiggyBank } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DetailHeader,
  StatTile,
  TrendChart,
  TopMerchantsCard,
  BreakdownCard,
  TransactionsTable,
} from '@/components/DetailBuildingBlocks';
import { formatMoney } from '@/lib/format';
import { colorForCategory } from '@/lib/categorize';
import { computeSummary } from '@/lib/detailHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const ACCOUNT_ICONS = {
  bank: Landmark,
  cash: Banknote,
  wallet: Wallet,
  credit_card: CreditCard,
  loan: CreditCard,
  deposit: PiggyBank,
};

/**
 * Phase 58B — AccountDetail rewired to the paginated backend API.
 *
 * Route: /accounts/:id/detail
 */
export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, session } = useAuth();
  const profileCurrency = profile?.currency || 'INR';

  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [period, setPeriod] = useState(90);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: '0' });
        if (period) params.set('period_days', String(period));
        const res = await axios.get(`${BACKEND_URL}/api/transactions/account/${encodeURIComponent(id)}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (cancelled) return;
        setData(res.data);
        setRows(res.data.rows || []);
        setHasMore(!!res.data.has_more);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setRows([]);
          setHasMore(false);
          setError(e?.response?.status === 404 ? 'Account not found' : 'Could not load account');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, period, session?.access_token]);

  const loadMore = async () => {
    if (!session?.access_token || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(rows.length) });
      if (period) params.set('period_days', String(period));
      const res = await axios.get(`${BACKEND_URL}/api/transactions/account/${encodeURIComponent(id)}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setRows((prev) => [...prev, ...(res.data.rows || [])]);
      setHasMore(!!res.data.has_more);
    } catch (e) { /* soft-fail */ } finally {
      setLoadingMore(false);
    }
  };

  const account = data?.account || null;
  const accountCurrency = account?.currency || profileCurrency;
  const accountName = account?.name || (loading ? 'Loading…' : (error || 'Account not found'));
  const Icon = ACCOUNT_ICONS[account?.type] || Wallet;
  const periodLabel = period === null ? 'All time' : `Last ${period} days`;

  const summary = data?.summary || {
    total_expense: 0, total_income: 0, total_invest: 0, net_flow: 0, count: 0, expense_count: 0,
  };
  const merchants = data?.top_merchants || [];
  const byCategory = (data?.breakdown_by_category || []).map((b) => ({ ...b }));
  const trend = computeSummary(rows, period).trend;
  const useMonthly = !period || period > 120;

  const subtitle = loading
    ? 'Loading account…'
    : account
      ? `${(account.type || 'bank').replace('_', ' ')} · Balance ${formatMoney(Number(account.balance || 0), accountCurrency, { compact: true })}`
      : '—';

  return (
    <div className="space-y-5">
      <DetailHeader
        icon={Icon}
        title={accountName}
        subtitle={subtitle}
        period={period}
        onPeriodChange={setPeriod}
        accent="hsl(var(--primary))"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatTile
          label="Inflows"
          value={formatMoney(summary.total_income, accountCurrency, { compact: true })}
          sub={periodLabel}
          testid="acc-stat-inflows"
        />
        <StatTile
          label="Outflows"
          value={formatMoney(summary.total_expense, accountCurrency, { compact: true })}
          sub={periodLabel}
          testid="acc-stat-outflows"
        />
        <StatTile
          label="Net flow"
          value={`${summary.net_flow < 0 ? '−' : ''}${formatMoney(Math.abs(summary.net_flow), accountCurrency, { compact: true })}`}
          sub={summary.net_flow < 0 ? 'Used more than came in' : 'Added more than went out'}
          testid="acc-stat-net"
        />
        <StatTile
          label="Transactions"
          value={String(summary.count)}
          sub={summary.total_invest > 0 ? `${formatMoney(summary.total_invest, accountCurrency, { compact: true })} moved to investments` : '—'}
          testid="acc-stat-count"
        />
      </div>

      <div className="rounded-2xl border border-border/70 bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">{useMonthly ? 'Monthly spend trend' : 'Weekly spend trend'}</div>
          <div className="text-xs text-muted-foreground">{periodLabel}</div>
        </div>
        <TrendChart data={trend} currency={accountCurrency} accent="hsl(var(--primary))" testid="acc-trend-chart" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <TopMerchantsCard rows={merchants} currency={accountCurrency} />
        <BreakdownCard
          title="By category"
          rows={byCategory}
          currency={accountCurrency}
          onRowClick={(catName) => {
            const slug = (byCategory.find((b) => b.key === catName) || {}).slug;
            if (slug) navigate(`/transactions/category/${slug}`);
          }}
          swatchFn={(name) => colorForCategory(name)}
          testid="acc-by-category"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Transactions</div>
          {data?.total_rows ? (
            <div className="text-xs text-muted-foreground">
              Showing {Math.min(rows.length, data.total_rows)} of {data.total_rows}
            </div>
          ) : null}
        </div>
        <TransactionsTable
          rows={rows}
          currency={accountCurrency}
          accounts={account ? [account] : []}
          emptyHint={loading ? 'Loading…' : 'No transactions for this account in the selected period.'}
        />
        {hasMore && (
          <div className="flex justify-center pt-1">
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="rounded-xl"
              data-testid="acc-show-more"
            >
              {loadingMore ? 'Loading…' : 'Show 50 more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
